import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router';
import { Button, Form, Input, Popconfirm, Typography } from 'antd';
import { Body2Light, Container, ExternalLink, Flex, Link, Text } from '@/ui';
import {
  useProduct,
  useProductLink,
  useProductSections
} from '@/features/constructorProducts/hooks';
import { ActionBox, Box, Space } from '@/features/constructorCommon/styled';
import { ProductSectionList } from '@/features/constructorProducts/components/ProductSectionList';
import { ProductSection } from '@/features/constructorProducts/components/ProductSection';
import { ProductImageUpload } from '@/features/constructorProducts/components/ProductImageUpload';
import { ProductFileDownloads } from '@/features/constructorProducts/components/ProductFileDownloads';
import {
  BASIC_FIELDS,
  PRODUCT_SPECIFIC_FIELDS
} from '@/features/constructorProducts/components/productFieldsConfig';
import {
  renderBasicFields,
  renderFieldsGroup,
  renderCheckboxes
} from '@/features/constructorProducts/components/productRenderHelpers';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { ConstructorForm } from '@/components/admin/molecules';
import { useProductDescription } from '@/features/constructorProducts/hooks/useProductDescription';
import { CollapseList } from '@/components/common/molecules/CollapseList';
import { COLORS } from '@/utils/static';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { UpdateProductLinkModal } from '@/components';

dayjs.extend(utc);

export const Product = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams();
  const {
    data,
    handleCreate,
    handleUpdate,
    handleDelete,
    handleFileUpload,
    isUpdating,
    isDeleting,
    handleFileDelete,
    refetch
  } = useProduct(id);
  const { handleCreate: handleCreateDescription, handleDelete: handleDeleteDescription } =
    useProductDescription();
  const { handleCreate: handleCreateLink, handleDelete: handleDeleteLink } = useProductLink();
  const { data: productSections } = useProductSections(id);
  const [showCreateNewSection, setShowCreateNewSection] = useState(false);
  const [showCreateDescriptionSection, setShowCreateDescriptionSection] = useState(false);
  const [showCreateNewLink, setShowCreateNewLink] = useState(false);
  const [product, setProduct] = useState({});
  const [productDescription, setProductDescription] = useState({});
  const [productLink, setProductLink] = useState({});
  const [selectedLink, setSelectedLink] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [image, setImage] = useState(null);
  const [files, setFiles] = useState([]);

  useEffect(() => {
    if (!data?.id) return;
    setProduct(data);
  }, [data]);

  const onChange = useCallback((e) => {
    const { name, value } = e.target;
    setProduct((prev) => ({ ...prev, [name]: value }));
  }, []);

  const onChangeNumber = useCallback((e) => {
    const { name, value } = e.target;
    if (!Number(value) && value.length > 0) return;
    setProduct((prev) => ({ ...prev, [name]: value }));
  }, []);

  const onChangeDate = useCallback((value, dateString) => {
    if (!dateString) {
      setProduct((prev) => ({ ...prev, offerPeriodUntil: null }));
    } else {
      const [day, month, year] = dateString.split('.');
      const isoDate = new Date(`${year}-${month}-${day}`);
      setProduct((prev) => ({ ...prev, offerPeriodUntil: dayjs.utc(isoDate).toISOString() }));
    }
  }, []);

  const onChangeIsCheckbox = useCallback((e) => {
    const { name, checked } = e.target;
    setProduct((prev) => ({ ...prev, [name]: checked }));
  }, []);

  const onChangeDescription = useCallback((e) => {
    const { name, value } = e.target;
    setProductDescription((prev) => ({ ...prev, [name]: value }));
  }, []);

  const onChangeLink = useCallback((e) => {
    const { name, value } = e.target;
    setProductLink((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleUploadFiles = useCallback(
    (name) => {
      if (files.length > 0 && product.id) {
        const promises = files.map((file) =>
          handleFileUpload({
            file: file,
            productId: product.id,
            name: name ?? file.name
          })
        );
        Promise.all(promises).then(() => setFiles([]));
      }
    },
    [files, product.id, handleFileUpload]
  );

  const handleAction = useCallback(() => {
    if (product.id) {
      handleUpdate({
        ...product,
        ...(image && { logoURL: image })
      }).then(() => {
        handleUploadFiles();
        refetch();
        setImage(null);
      });
    } else {
      handleCreate(product).then((res) => {
        refetch();
        if (res?.data?.id) {
          navigate(`/constructorProducts/${res.data.id}`);
        }
      });
    }
  }, [product, image, handleUpdate, handleCreate, handleUploadFiles, refetch, navigate]);

  const handleAddDescriptionSection = useCallback(() => {
    handleCreateDescription({ productId: product.id, ...productDescription }).then(() => {
      setProductDescription({});
      setShowCreateDescriptionSection(false);
    });
  }, [product, productDescription, handleCreateDescription]);

  const handleDeleteDescriptionSection = useCallback(
    (id) => {
      handleDeleteDescription({ productDescriptionId: id });
    },
    [handleDeleteDescription]
  );

  const handleDeleteFile = useCallback(
    (fileId) => {
      handleFileDelete(fileId);
    },
    [handleFileDelete]
  );

  const handleAddNewLink = useCallback(() => {
    handleCreateLink({ productId: product.id, ...productLink }).then(() => {
      setProductLink({});
      setShowCreateNewLink(false);
    });
  }, [product, productLink, handleCreateLink]);

  const handleDeleteNewLink = useCallback(
    (id) => {
      handleDeleteLink({ productLinkId: id });
    },
    [handleDeleteLink]
  );

  const onDelete = useCallback(() => {
    handleDelete(product);
    navigate(`/constructorProducts/`);
  }, [product, handleDelete, navigate]);

  const isDisabled = !product.type;

  const handlers = {
    onChange,
    onChangeNumber,
    onChangeDate,
    onChangeIsCheckbox
  };

  return (
    <Container $paddingTop={20} $paddingBottom={40}>
      {product.id && (
        <Container $marginBottom={30}>
          <Typography.Title level={3}>{t('Constructor (update product)')}</Typography.Title>
        </Container>
      )}
      <ConstructorForm>
        <Flex vertical gap={15}>
          {product.id && (
            <Container $paddingTop={15}>
              <Typography.Title level={4}>{t('Main parameters')}</Typography.Title>
            </Container>
          )}
          <Box
            $isNew={true}
            styles={{
              body: {
                padding: '16px'
              }
            }}
          >
            <ProductImageUpload
              productId={product.id}
              image={image}
              setImage={setImage}
              logoURL={product.logoURL}
              t={t}
            />
            {renderBasicFields(BASIC_FIELDS, product, handlers, t)}
          </Box>

          {product.id && (
            <Container $paddingTop={15}>
              <Typography.Title level={4}>{t('Description')}</Typography.Title>
            </Container>
          )}

          {product.id && (
            <Box
              $isNew={true}
              styles={{
                body: {
                  padding: '16px'
                }
              }}
            >
              {product.id ? (
                <Container $marginBottom={20}>
                  <Form.Item label={t('Main description')}>
                    <Input.TextArea
                      name={'descriptions'}
                      value={product.descriptions}
                      onChange={onChange}
                      rows={3}
                    />
                  </Form.Item>
                  <Form.Item label={`${t('Main description')} (RU)`}>
                    <Input.TextArea
                      name={'descriptions_RU'}
                      value={product.descriptions_RU}
                      onChange={onChange}
                      rows={3}
                    />
                  </Form.Item>
                  <Form.Item label={`${t('Main description')} (LV)`}>
                    <Input.TextArea
                      name={'descriptions_LV'}
                      value={product.descriptions_LV}
                      onChange={onChange}
                      rows={3}
                    />
                  </Form.Item>

                  <Flex vertical gap={15} $marginTop={28} $marginLeft={15}>
                    <Text>{t('Collapsed description sections')}</Text>
                    <Box
                      styles={{
                        body: {
                          padding: '0 16px'
                        }
                      }}
                    >
                      <CollapseList
                        items={product.productDescriptions.map((item) => ({
                          key: item.id,
                          label: item.name,
                          children: <p>{item.descriptions}</p>
                        }))}
                        productDescriptions={product.productDescriptions}
                        adminView
                        onDelete={handleDeleteDescriptionSection}
                      />
                    </Box>
                  </Flex>

                  <Container $marginTop={15} $marginLeft={15}>
                    {showCreateDescriptionSection ? (
                      <Box
                        styles={{
                          body: {
                            padding: '16px'
                          }
                        }}
                      >
                        <Form.Item label={t('Title')}>
                          <Input
                            name={'name'}
                            value={productDescription.name}
                            onChange={onChangeDescription}
                          />
                        </Form.Item>
                        <Form.Item label={t('Description')}>
                          <Input.TextArea
                            name={'descriptions'}
                            value={productDescription.descriptions}
                            onChange={onChangeDescription}
                            rows={3}
                          />
                        </Form.Item>
                        <Container $marginTop={16}>
                          <Button
                            disabled={!productDescription.name}
                            type='primary'
                            onClick={handleAddDescriptionSection}
                          >
                            {t('Add a new section')}
                          </Button>
                        </Container>
                      </Box>
                    ) : (
                      <Button onClick={() => setShowCreateDescriptionSection(true)}>
                        {t('Add a collapsed section')}
                      </Button>
                    )}
                  </Container>
                </Container>
              ) : null}
            </Box>
          )}

          {product.id && (
            <Container $paddingTop={15}>
              <Typography.Title level={4}>{t('Additional parameters')}</Typography.Title>
            </Container>
          )}

          {product.id && (
            <Box
              $isNew={true}
              styles={{
                body: {
                  padding: '16px'
                }
              }}
            >
              {renderFieldsGroup(PRODUCT_SPECIFIC_FIELDS, product, handlers, t, true)}
              {renderCheckboxes(product, onChangeIsCheckbox, t)}
            </Box>
          )}

          <ActionBox>
            <Button
              onClick={handleAction}
              disabled={isDisabled}
              type='primary'
              loading={isUpdating}
            >
              {product.id ? t('Update Product') : t('Create Product')}
            </Button>
            {product.id && (
              <Popconfirm
                placement='topRight'
                title={t('Are you sure to delete this product?')}
                onConfirm={onDelete}
                okText={t('Yes')}
                cancelText={t('No')}
              >
                <Button danger loading={isDeleting}>
                  {t('Delete Product')}
                </Button>
              </Popconfirm>
            )}
          </ActionBox>
        </Flex>
      </ConstructorForm>

      {product.id && (
        <>
          <Container $paddingTop={30} $paddingBottom={10}>
            <ProductSectionList productId={id} />
            <Space />

            {productSections?.length === 0 ? (
              <>
                {showCreateNewSection ? (
                  <ProductSection onCreated={() => setShowCreateNewSection(false)} />
                ) : (
                  <Button onClick={() => setShowCreateNewSection(true)}>
                    {t('Add new Section')}
                  </Button>
                )}
              </>
            ) : null}
          </Container>

          <ProductFileDownloads
            product={product}
            files={files}
            setFiles={setFiles}
            onDeleteFile={handleDeleteFile}
            onUploadFiles={handleUploadFiles}
            t={t}
          />

          <Container $paddingTop={25}>
            <Typography.Title level={4}>{t('Links')}</Typography.Title>

            <Flex vertical gap={15} $marginTop={28} $marginBottom={30} $marginLeft={15}>
              {product.productLinks?.map((link, index) => (
                <Flex justify='space-between' gap={10}>
                  <Flex gap={10}>
                    <Body2Light $color={COLORS.blueAntd} style={{ flexShrink: 0 }}>
                      {index}.
                    </Body2Light>
                    <Flex vertical>
                      <Body2Light>{link.name}</Body2Light>
                      <ExternalLink $fontSize={'14px'}>{link.link}</ExternalLink>
                    </Flex>
                  </Flex>

                  <Flex gap={15} align={'center'} style={{ fontSize: '16px' }}>
                    <EditOutlined
                      onClick={(event) => {
                        event.stopPropagation();
                        setSelectedLink(link);
                        setOpenModal(true);
                      }}
                    />
                    <DeleteOutlined
                      onClick={(event) => {
                        event.stopPropagation();
                        handleDeleteNewLink(link.id);
                      }}
                    />
                  </Flex>
                </Flex>
              ))}
              <UpdateProductLinkModal
                open={openModal}
                onClose={() => setOpenModal(false)}
                selectedItem={selectedLink}
                productDescriptions={product.productLinks}
              />
            </Flex>

            <Container $marginTop={16}>
              {showCreateNewLink ? (
                <Box
                  styles={{
                    body: {
                      padding: '16px'
                    }
                  }}
                >
                  <Form.Item label={t('Title')}>
                    <Input name={'name'} value={productLink.name} onChange={onChangeLink} />
                  </Form.Item>
                  <Form.Item label={t('Link')}>
                    <Input.TextArea
                      name={'link'}
                      value={productLink.link}
                      onChange={onChangeLink}
                      rows={3}
                    />
                  </Form.Item>
                  <Container $marginTop={16}>
                    <Button disabled={!productLink.name} type='primary' onClick={handleAddNewLink}>
                      {t('Add a new link')}
                    </Button>
                  </Container>
                </Box>
              ) : (
                <Button onClick={() => setShowCreateNewLink(true)}>{t('Add a new link')}</Button>
              )}
            </Container>
          </Container>
        </>
      )}
    </Container>
  );
};
